import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { pushLocked } from "@/components/InstallGuide";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const toKey = (b64) => {
  const s = atob((b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
};
const registration = async () => (await navigator.serviceWorker.getRegistration()) || navigator.serviceWorker.register("/sw.js");

// Re-links this device's existing subscription to the signed-in account (fixes devices the server no longer knows).
export async function syncPush() {
  if (!supported() || Notification.permission !== "granted") return null;
  const reg = await navigator.serviceWorker.getRegistration();
  const s = reg && (await reg.pushManager.getSubscription());
  if (s) await api.post("/push/subscribe", s.toJSON());
  return s || null;
}

export default function PushToggle({ onChange }) {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (supported()) { syncPush().then((s) => { setSub(s); onChange?.(!!s); }).catch(() => {}); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (pushLocked()) {
    return <p className="text-xs text-amber-200" data-testid="push-locked">Installe d'abord l'app sur ton téléphone, puis ouvre-la depuis l'icône : tu pourras alors activer les alertes.</p>;
  }
  if (!supported()) {
    return <p className="text-xs text-slate-400" data-testid="push-unsupported">{isIOS() && !standalone()
      ? "Sur iPhone, ajoute d'abord Ma Petite Ligue à l'écran d'accueil (iOS 16.4 ou plus), puis ouvre-la depuis l'icône pour activer les notifications."
      : "Ce navigateur ne permet pas les notifications sur cet appareil."}</p>;
  }
  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") { toast.error("Notifications refusées. Autorise-les dans les réglages du téléphone."); return; }
      const reg = await registration();
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const s = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.key) }));
      await api.post("/push/subscribe", s.toJSON());
      setSub(s);
      onChange?.(true);
      toast.success("Notifications activées sur cet appareil");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); await sub.unsubscribe(); setSub(null); onChange?.(false); toast.success("Notifications désactivées sur cet appareil"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.delivered) toast.success("Alerte de test envoyée : elle doit apparaître dans quelques secondes");
      else toast.error("Aucun appareil inscrit : appuie sur « Activer les notifications »");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2" data-testid="push-toggle">
      <span className="text-sm" data-testid="push-state">{sub ? "Activées sur cet appareil" : "Désactivées sur cet appareil"}</span>
      <div className="flex flex-wrap gap-2">
        {sub && <button onClick={test} disabled={busy} className="mpl-btn-ghost" data-testid="push-test"><Send size={14} /> M'envoyer un test</button>}
        <button onClick={sub ? disable : enable} disabled={busy} className={sub ? "mpl-btn-ghost" : "mpl-btn-primary"} data-testid={sub ? "push-disable" : "push-enable"}>
          <BellRing size={15} /> {sub ? "Désactiver" : "Activer les notifications"}
        </button>
      </div>
    </div>
  );
}
